import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Camera, Loader2 } from 'lucide-react'
import { turbineService, type Photo } from '@/services/turbineService'
import { ErrorBanner } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { log360, photoMatchesBlade, isCategoryFolderPhoto, buildImageProxyUrl } from '../shared'

// TODO(i18n): 360° görüntüleyiciye özel metinler. strings.ts bu birimin sahibi
// olmadığı için metinler şimdilik burada, doğru Türkçe diakritiklerle tutuluyor.
const PHOTOS_LOAD_ERROR = 'Panoramik fotoğraf listesi yüklenemedi.'
const VIEWER_INIT_ERROR = '360° görüntüleyici başlatılamadı. Sayfayı yenilemeyi deneyin.'
const PANORAMA_LOAD_ERROR = '360° görüntü açılamadı. Bağlantınızı kontrol edip tekrar deneyin.'
const SAVE_ERROR_TITLE = 'Kaydetme hatası'
const SAVE_ERROR_BODY = 'Fotoğraf kaydedilirken hata oluştu. Lütfen tekrar deneyin.'

// Panolens.js-powered 360° photo viewer with navigation
const Enhanced360Viewer: React.FC<{
  turbineId: number,
  roomIndex: number,
  // Sidebar ile aynı blade filtresi uygulanır ki iki liste birebir aynı sırada
  // olsun; böylece gezinme seçili blade+oda içinde 1,2,3,4 sıralı ilerler.
  selectedBlade: string,
  selectedPhoto?: Photo | null,
  onPhotoChange?: (photo: Photo) => void,
  isSidebarOpen?: boolean,
  onRefresh?: () => void,
  onVersionsRefresh?: () => void,
  loadingVersions?: boolean
}> = ({ turbineId, roomIndex, selectedBlade, selectedPhoto, onPhotoChange, isSidebarOpen, onRefresh, onVersionsRefresh }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isViewerReady, setIsViewerReady] = useState(false);
  // Panorama-to-panorama geçişte doku yüklenirken gösterilir. Oda içi hızlı
  // (cache'li) geçişte cross-fade zaten pürüzsüz olduğu için 150ms gecikmeyle
  // açılır; blade/oda değişiminde (soğuk POT resize) siyah boşluğu kapatır.
  const [isPanoramaLoading, setIsPanoramaLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const currentPanoramaRef = useRef<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const lastLoadedUrlRef = useRef<string | null>(null);
  // Spinner durumu, effect-run'a değil YÜKLENEN panorama'ya bağlanır ki tetikleme
  // yolu (sidebar tıklaması vs. ok tuşu) davranışı değiştirmesin. pendingPanoramaRef
  // = şu an dokusu beklenen panorama; spinnerTimerRef = gecikme/emniyet timer'ı.
  const pendingPanoramaRef = useRef<any>(null);
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // photoVersions, currentVersionIndex, and loadingVersions are now managed by parent component

  const { t } = useLanguage();
  // Bildirimler global toast katmanından (NotificationProvider) geliyor.
  // Eski lokal ToastNotification'ın `type` prop'u YOKTU: hatalar da yeşil
  // "başarılı" kutusunda gösteriliyordu.
  const { notifyError, notifyWarning, notifySuccess } = useNotifications();

  // "Hata != boş veri": foto listesi yüklenemezse photos sıfırlanmaz; ayrı bir
  // hata state'i tutulur, mevcut veri korunur ve "Tekrar dene" sunulur.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [photosReloadKey, setPhotosReloadKey] = useState(0);
  // Panolens/WebGL tarafı: görüntüleyici hiç kurulamadıysa ya da doku
  // yüklenemediyse kullanıcı sebepsiz siyah ekrana bakmasın.
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [panoramaError, setPanoramaError] = useState<string | null>(null);
  const [panoramaRetryKey, setPanoramaRetryKey] = useState(0);
  // Çift gönderim koruması (Kaydet).
  const [isSaving, setIsSaving] = useState(false);

  // Panorama'yı yeniden dener. Kimlik önbelleği sıfırlanır ki effect aynı
  // görüntüyü "zaten yüklü" sayıp atlamasın. Panolens dispose mantığına
  // DOKUNULMAZ: yalnızca yeni bir ImagePanorama yüklenir.
  const retryPanorama = useCallback(() => {
    lastLoadedUrlRef.current = null;
    setPanoramaError(null);
    setPanoramaRetryKey(prev => prev + 1);
  }, []);

  // Drawing tools state - Canvas-based for 360° compatibility
  const [isDrawingMode, setIsDrawingMode] = useState(false);

  const [drawingTool, setDrawingTool] = useState<'pen' | 'circle' | 'rectangle' | 'text'>('pen');
  const [drawings, setDrawings] = useState<any[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<Array<{x: number, y: number, textureX?: number, textureY?: number}>>([]);
  const [startPoint, setStartPoint] = useState<{x: number, y: number, textureX?: number, textureY?: number} | null>(null);
  const [textInput, setTextInput] = useState('');
	  const [showTextInput, setShowTextInput] = useState(false);
	  const [textPosition, setTextPosition] = useState<{x: number, y: number, textureX?: number, textureY?: number} | null>(null);
	  const [textModalPosition, setTextModalPosition] = useState<{x: number, y: number} | null>(null);
	  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
	  const [canvasSize, setCanvasSize] = useState<{width: number, height: number}>({width: 0, height: 0});

	  // Step 2: Initialize Panolens viewer ONCE and keep it alive.
	  // Eskiden viewer, isLoading/photos.length her değiştiğinde dispose+recreate
	  // ediliyordu; bu, panorama'nın async doku yüklemesiyle yarışıp panolens'i
	  // çökertiyordu (setCameraControl/onFadeAnimationUpdate/updateTexture null
	  // crash'leri) ve blade/oda geçişinde siyah boşluk bırakıyordu. Kalıcı
	  // viewer'da geçişler yalnızca setPanorama (cross-fade) ile olur; eski görsel
	  // yenisi gelene kadar durur. (Save sonrası parent key={viewerRefreshKey} ile
	  // komple remount → viewer yine baştan yaratılır.)
	  useEffect(() => {
	    if (viewerRef.current) {
	      return; // already created — persists for the component's lifetime
	    }

	    if (!containerRef.current) {
	      console.error('❌ Viewer initialization failed: Container not ready.');
	      setViewerError(VIEWER_INIT_ERROR);
	      return;
	    }

	    // Wait for PANOLENS to be fully loaded
	    const checkPanolensReady = () => {
	      const panolens = (window as any).PANOLENS;
	      if (!panolens) {
	        console.error('❌ PANOLENS library not found on window object');
	        return false;
	      }
	      if (!panolens.Viewer) {
	        console.error('❌ PANOLENS.Viewer not found');
	        return false;
	      }
	      return true;
	    };

	    if (!checkPanolensReady()) {
	      // Retry after a short delay if PANOLENS isn't ready yet
	      setTimeout(() => {
	        if (checkPanolensReady()) {
	          initializeViewer();
	        } else {
	          console.error('❌ PANOLENS library failed to load after retry');
	          // Kütüphane hiç gelmediyse ekran kalıcı olarak boş kalır: kullanıcıya söyle.
	          setViewerError(VIEWER_INIT_ERROR);
	        }
	      }, 100);
	      return;
	    }

	    const initializeViewer = () => {
	      try {
	        log360('Creating PANOLENS viewer')

	        const panolens = (window as any).PANOLENS;
	        const viewer = new panolens.Viewer({
	          container: containerRef.current,
	          controlBar: false,
	          autoRotate: false,
	          output: 'console'
	        });

	        viewerRef.current = viewer;
	        setIsViewerReady(true); // Signal that viewer is ready
	        setViewerError(null);
	        log360('PANOLENS viewer created successfully')
	      } catch (error) {
	        console.error('❌ Error creating viewer:', error);
	        // Try fallback initialization without control bar
	        try {
	          log360('Attempting fallback viewer initialization')
	          const panolens = (window as any).PANOLENS;
	          const viewer = new panolens.Viewer({
	            container: containerRef.current,
	            output: 'console'
	          });
	          viewerRef.current = viewer;
	          setIsViewerReady(true);
	          setViewerError(null);
	          log360('Fallback PANOLENS viewer created successfully')
	        } catch (fallbackError) {
	          console.error('❌ Fallback viewer creation also failed:', fallbackError);
	          // İki deneme de başarısız: viewer hiç oluşmadı, ekran boş kalacak.
	          setViewerError(VIEWER_INIT_ERROR);
	        }
	      }
	    };

	    initializeViewer();

	    return () => {
	      if (viewerRef.current) {
	        log360('Disposing PANOLENS viewer')
	        try {
	          viewerRef.current.dispose();
	        } catch (error) {
	          // Bilerek sessiz: unmount temizliği. Kullanıcı bu ekrandan zaten ayrıldı,
	          // gösterilecek bir yüzey ve alınacak bir aksiyon yok.
	          console.error('Error disposing viewer:', error);
	        }
	        viewerRef.current = null;
	        setIsViewerReady(false);
	        // Disposed viewer'ın panorama'ları da gitti; ref'i sıfırla ki bir
	        // sonraki viewer, önceki (artık dispose edilmiş) panorama'yı silmeye
	        // çalışmasın.
	        currentPanoramaRef.current = null;
	        lastLoadedUrlRef.current = null;
	      }
	    };
	  }, []); // create once on mount; disposed only on unmount (or refreshKey remount)

	  // Step 1: Load photos for the current room (force refresh to get latest)
	  useEffect(() => {
	    let cancelled = false

	    const normalizeRoomNumber = (value: unknown): number | null => {
	      if (value === null || value === undefined) return null
	      const parsed = Number.parseInt(String(value), 10)
	      return Number.isNaN(parsed) ? null : parsed
	    }

	    const loadPhotos = async () => {
	      setIsLoading(true)
	      log360('Loading photos for room', { turbineId, roomIndex })

	      // "Hata != boş veri": istek başarısız olursa liste sıfırlanmaz. Ekrandaki
	      // foto seti korunur, ayrı bir hata mesajı + "Tekrar dene" gösterilir.
	      let failureMessage: string | null = null

	      try {
	        // Normal caching: the sidebar fetches the same lists, so cache hits make
	        // room/blade switches instant instead of regenerating presigned URLs.
	        const roomResponse = await turbineService.getPhotos(turbineId, roomIndex)
	        let photoList: Photo[] =
	          roomResponse.success && Array.isArray(roomResponse.data) ? roomResponse.data : []

	        // success:false BİR HATADIR; eskiden sessizce boş listeye düşülüyordu.
	        if (!roomResponse.success) {
	          failureMessage = responseErrorMessage(roomResponse, t, PHOTOS_LOAD_ERROR)
	        }

	        if (photoList.length === 0) {
	          log360('Room photo list empty; falling back to all photos', {
	            turbineId,
	            roomIndex,
	            message: roomResponse.message
	          })

	          const allResponse = await turbineService.getPhotos(turbineId)
	          if (allResponse.success && Array.isArray(allResponse.data)) {
	            // Yedek istek çalıştı: oda isteğinin hatası kullanıcıyı ilgilendirmez.
	            failureMessage = null
	            const targetRoom = normalizeRoomNumber(roomIndex)
	            photoList = allResponse.data.filter(photo => normalizeRoomNumber(photo.room) === targetRoom)
	          } else {
	            failureMessage = responseErrorMessage(allResponse, t, PHOTOS_LOAD_ERROR)
	          }
	        }

	        // Blade + kategori filtresi: sidebar ile BİREBİR aynı foto seti üretilir,
	        // böylece sidebar index N == viewer index N ve gezinme sıralı ilerler.
	        const bladePhotos = photoList.filter(
	          photo => !isCategoryFolderPhoto(photo) && photoMatchesBlade(photo, selectedBlade)
	        )

	        const sortedPhotos = [...bladePhotos].sort((a, b) =>
	          (a.originalName || '').localeCompare(b.originalName || '', undefined, { numeric: true })
	        )

	        if (!cancelled) {
	          if (failureMessage) {
	            // Mevcut foto seti KORUNUR; boş liste hatayı "fotoğraf yok" gibi gösterirdi.
	            console.error('❌ Panorama photo list request failed:', failureMessage)
	            setLoadError(failureMessage)
	          } else {
	            setLoadError(null)
	            setPhotos(sortedPhotos)
	            log360('Photos loaded', { count: sortedPhotos.length })
	            setDrawings([])
	          }
	        }
	      } catch (error) {
	        if (!cancelled) {
	          logError('Turbine360.loadPhotos', error)
	          // Liste [] YAPILMIYOR: hata, "bu odada fotoğraf yok" gibi görünmemeli.
	          setLoadError(toUserMessage(error, t, PHOTOS_LOAD_ERROR))
	        }
	      } finally {
	        if (!cancelled) {
	          setIsLoading(false)
	        }
	      }
	    }

	    loadPhotos()

	    return () => {
	      cancelled = true
	    }
	  }, [turbineId, roomIndex, selectedBlade, photosReloadKey]);

  // Reset photo index when photos array changes (blade/room change)
  useEffect(() => {
    if (photos.length === 0) {
      setCurrentPhotoIndex(0);
    } else if (currentPhotoIndex >= photos.length) {
      setCurrentPhotoIndex(0); // Reset to first photo if current index is out of bounds
    }
  }, [photos.length]);

  // Set the initial photo index when a photo is selected from the sidebar
  useEffect(() => {
    if (photos.length > 0 && selectedPhoto) {
      const targetKey = (selectedPhoto as any)._originalKey || selectedPhoto.key;
      const selectedIndex = photos.findIndex(p => p.key === targetKey);
      if (selectedIndex !== -1 && selectedIndex !== currentPhotoIndex) {
        setCurrentPhotoIndex(selectedIndex);
        log360('Selected photo matched, updating index', { selectedIndex, targetKey });
        }
    }
  }, [selectedPhoto, photos, currentPhotoIndex]);

  // Step 3: Load the panorama image into the viewer when the viewer is ready and the photo changes
  useEffect(() => {
    // Spinner'ı durdur + bekleyen timer'ı temizle (yükleme bitti / iptal / foto yok).
    const stopSpinner = () => {
      pendingPanoramaRef.current = null;
      if (spinnerTimerRef.current) { clearTimeout(spinnerTimerRef.current); spinnerTimerRef.current = null; }
      setIsPanoramaLoading(false);
    };

    if (!isViewerReady || !viewerRef.current || photos.length === 0) {
      log360('Viewer not ready or no photos', { isViewerReady, photoCount: photos.length });
      // Viewer kalıcı; burada panorama'yı KALDIRMIYORUZ (kaldırmak panolens'in
      // this.panorama'sını null yapıp animate loop'u çökertirdi). Foto yoksa
      // render'daki "Bu odada fotoğraf bulunamadı" overlay'i son panorama'nın
      // üstünü kapatır. Ref'leri de sıfırlamıyoruz ki foto geri gelince önceki
      // panorama düzgün (leave-complete'te) dispose edilebilsin.
      stopSpinner();
      return;
    }

    // Resolve which photo to render. A selected photo (version-aware URL) wins
    // whenever it belongs to the current blade+room list — even if
    // currentPhotoIndex hasn't synced yet — so we never briefly load the
    // previous photo. This is the core of the intermittent-load fix.
    const active = (() => {
      if (selectedPhoto) {
        const baseKey = (selectedPhoto as any)._originalKey || selectedPhoto.key
        const match = photos.find(p => p.key === baseKey)
        if (match) return { photo: match, url: selectedPhoto.url || match.url }
      }
      const indexed = photos[currentPhotoIndex]
      return indexed ? { photo: indexed, url: indexed.url } : null
    })()

    if (!active || !active.url) {
      log360('No active photo to render', { currentPhotoIndex });
      stopSpinner();
      return;
    }

    // Identity = photo key + signature-stripped URL. Distinguishes different
    // photos AND versions (new "_vN" key/url) while ignoring the per-request
    // presigned params. Compared on the RAW Wasabi URL (not the proxy URL),
    // so it stays stable even though every proxy request re-signs.
    const identity = `${active.photo.key}|${active.url.split('?')[0]}`
    if (identity === lastLoadedUrlRef.current && currentPanoramaRef.current) {
      // Aynı panorama zaten hedef. Yüklendiyse spinner'ı kapat; hâlâ yükleniyorsa
      // DOKUNMA — kendi settle'ı kapatacak. Böylece sidebar'ın ekstra re-run'ları
      // (index-sync) spinner'ı iptal etmez → ok tuşlarıyla AYNI davranış.
      log360('Skipping panorama load, same image already displayed', { identity });
      if (currentPanoramaRef.current.userData?.__settled) stopSpinner();
      return;
    }

    try {
      const panolens = (window as any).PANOLENS;
      if (!panolens || !panolens.ImagePanorama) {
        throw new Error('PANOLENS.ImagePanorama not available');
      }

      // Same-origin proxy + power-of-two resize (pano=1): eliminates per-bucket
      // Wasabi CORS fragility and the NPOT black-sphere bug on WebGL1. The
      // presigned signature is preserved inside the proxied `url` param, so no
      // 403 — and we must NOT add cache-busting params to the presigned URL.
      const finalUrl = buildImageProxyUrl(active.url, { pano: true })
      log360('Requesting panorama via proxy', { key: active.photo.key, finalUrl })

      const viewer = viewerRef.current;
      const previousPanorama = currentPanoramaRef.current;

      // Yeni bir panorama denemesi başlıyor: önceki denemenin hatası temizlenir.
      setPanoramaError(null);

      const panorama = new panolens.ImagePanorama(finalUrl);

      // Yükleme göstergesini BU panorama'ya bağla (ref tabanlı). Effect re-run'ları
      // (sidebar tıklamasındaki index-sync gibi) timer'ı iptal edemez; tetikleme
      // yolundan bağımsız TUTARLI davranış. 150ms gecikmeyle açılır (cache'ten
      // anında gelende flash olmasın), doku settle olunca kapanır.
      pendingPanoramaRef.current = panorama;
      if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
      spinnerTimerRef.current = setTimeout(() => {
        if (pendingPanoramaRef.current === panorama && !panorama.userData.__settled) {
          setIsPanoramaLoading(true);
          // Emniyet: settle hiç gelmezse (ağ takılması) spinner takılı kalmasın.
          spinnerTimerRef.current = setTimeout(() => {
            if (pendingPanoramaRef.current === panorama) stopSpinner();
          }, 20000);
        }
      }, 150);

      const settle = () => {
        panorama.userData.__settled = true;
        if (pendingPanoramaRef.current === panorama) stopSpinner();
      };
      panorama.addEventListener('load', () => {
        log360('Panorama texture loaded', { key: active.photo.key });
        if (currentPanoramaRef.current === panorama) setPanoramaError(null);
        settle();
      });
      panorama.addEventListener('error', () => {
        console.error('❌ Panorama texture failed to load:', active.photo.key);
        // Doku gelmezse ekranda hiçbir şey değişmiyordu (siyah küre). Hatayı yüzeye
        // çıkar; yalnızca HÂLÂ hedef olan panorama için (iptal edilmiş eski bir
        // yüklemenin hatası kullanıcıyı yanıltmasın).
        if (currentPanoramaRef.current === panorama) {
          setPanoramaError(PANORAMA_LOAD_ERROR);
        }
        settle();
      });

      // Dispose the PREVIOUS panorama to cap GPU memory (POT textures), but only
      // when it is BOTH (a) done fading out and (b) done loading its texture.
      // Two crashes are avoided:
      //  - dispose during the leave-fade → material nulled while the fade tween
      //    reads material.opacity → onFadeAnimationUpdate crash. Fixed by waiting
      //    for 'leave-complete'.
      //  - dispose while the image is still in-flight → material nulled before
      //    onLoad runs updateTexture (this.material.map = …) → updateTexture
      //    crash. Fixed by waiting until the texture has settled (load/error).
      // The guard also skips disposal if it became panolens's active panorama.
      if (previousPanorama && previousPanorama !== panorama) {
        const disposePrevious = () => {
          previousPanorama.removeEventListener('leave-complete', disposePrevious);
          const doDispose = () => {
            if (viewer.panorama === previousPanorama) return; // active again — keep it
            try {
              viewer.remove(previousPanorama);
              previousPanorama.dispose?.();
            } catch (err) {
              console.error('Error disposing previous panorama:', err);
            }
          };
          if (previousPanorama.userData?.__settled) {
            doDispose();
          } else {
            // Texture henüz yüklenmedi; settle olunca dispose et (panolens'in
            // updateTexture'ı bizim listener'dan önce çalışır, material set edilir).
            previousPanorama.addEventListener('load', doDispose);
            previousPanorama.addEventListener('error', doDispose);
          }
        };
        previousPanorama.addEventListener('leave-complete', disposePrevious);
      }

      viewer.add(panorama);
      viewer.setPanorama(panorama); // this.panorama = new → never null (no crash)

      currentPanoramaRef.current = panorama;
      lastLoadedUrlRef.current = identity;
    } catch (error) {
      logError('Turbine360.loadPanorama', error);
      // Eskiden yalnızca spinner durduruluyordu: kullanıcı sebepsiz siyah ekrana bakıyordu.
      setPanoramaError(toUserMessage(error, t, PANORAMA_LOAD_ERROR));
      stopSpinner();
    }
  }, [isViewerReady, currentPhotoIndex, photos, selectedPhoto, panoramaRetryKey]);

  // Unmount'ta bekleyen spinner timer'ını temizle (unmount sonrası setState önler).
  useEffect(() => () => {
    if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
  }, []);

  // Fullscreen functions - a cleaner implementation
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        // Bilerek sessiz: tam ekrandan çıkış ESC ile de mümkün ve ekranın durumu
        // kendini anlatıyor; kullanıcının alabileceği bir aksiyon yok.
        console.error('Error exiting fullscreen:', err);
      });
    } else {
      el.requestFullscreen().catch(err => {
        // Eskiden ham İngilizce err.message/err.name alert() ile dökülüyordu.
        console.error('Error entering fullscreen:', err);
        notifyWarning('Tam ekran açılamadı', 'Tarayıcınız bu görünüm için tam ekran moduna izin vermedi.') // TODO(i18n)
      });
    }
  };
  
  // Listen for fullscreen changes from any source (button click, ESC key, etc.)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      
      // Force resize the viewer container and Panolens viewer
      if (viewerRef.current && containerRef.current) {
        // Multiple resize attempts to ensure proper sizing
        setTimeout(() => {
          if (viewerRef.current) {
            viewerRef.current.onWindowResize();
          }
        }, 50);
        
        setTimeout(() => {
          if (viewerRef.current) {
            viewerRef.current.onWindowResize();
            // Force container to recalculate its dimensions
            const container = containerRef.current;
            if (container) {
              container.style.width = '100%';
              container.style.height = '100%';
            }
          }
        }, 150);
        
        // Final resize after DOM has settled
        setTimeout(() => {
          if (viewerRef.current) {
            viewerRef.current.onWindowResize();
          }
          // Trigger a window resize event to ensure everything updates
          window.dispatchEvent(new Event('resize'));
        }, 300);
      }
    };
  
    document.addEventListener('fullscreenchange', handleFullscreenChange);
  
    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []); // Empty dependency array ensures this runs only once

  // Handle sidebar toggle resize
  useEffect(() => {
    if (viewerRef.current && containerRef.current) {
      // Force container to fill its parent immediately
      const container = containerRef.current;
      const resizeContainer = () => {
        if (container && container.parentElement) {
          container.style.width = '100%';
          container.style.height = '100%';
          
          // Force Panolens viewer to resize
          if (viewerRef.current) {
            viewerRef.current.onWindowResize();
          }
        }
      };

      // Immediate resize
      resizeContainer();
      window.dispatchEvent(new Event('resize'));
      
      // Multiple resize attempts to ensure proper sizing
      const timeouts = [50, 150, 300, 500, 800].map(delay =>
        setTimeout(() => {
          resizeContainer();
          window.dispatchEvent(new Event('resize'));
        }, delay)
      );

      return () => {
        timeouts.forEach(clearTimeout);
      };
    }
    return undefined;
  }, [isSidebarOpen]); // Dependency on sidebar state

  // Update canvas size to perfectly match panorama container
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newSize = {
          width: Math.floor(rect.width),
          height: Math.floor(rect.height)
        };

        if (newSize.width > 0 && newSize.height > 0 &&
            (newSize.width !== canvasSize.width || newSize.height !== canvasSize.height)) {
          setCanvasSize(newSize);

          // Canvas element'ini de güncelle
          if (drawingCanvasRef.current) {
            const canvas = drawingCanvasRef.current;
            canvas.width = Math.floor(newSize.width);
            canvas.height = Math.floor(newSize.height);
            // CSS boyutunu da pixel olarak ayarla - koordinat kaymasını önler
            canvas.style.width = `${Math.floor(newSize.width)}px`;
            canvas.style.height = `${Math.floor(newSize.height)}px`;

            // Canvas'ı temizle ve yeniden çiz
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
          }
        }
      }
    };

    // Initial update
    updateCanvasSize();

    // Multiple delayed updates to ensure proper sizing
    const timeouts = [50, 150, 300, 500, 800].map(delay =>
      setTimeout(updateCanvasSize, delay)
    );

    window.addEventListener('resize', updateCanvasSize);

    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [isSidebarOpen, isFullscreen, isViewerReady, photos.length, canvasSize.width, canvasSize.height]);

  // Redraw all drawings on canvas whenever drawings or canvas size changes
  useEffect(() => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    // Clear canvas with transparent background
    ctx.clearRect(0, 0, w, h);

    // Draw all existing drawings - convert percentage (0-1) to pixels
    drawings.forEach(drawing => {
      ctx.save(); // Save context state
      ctx.strokeStyle = drawing.color || '#ff0000';
      ctx.lineWidth = Math.max(3, drawing.strokeWidth || 4);
      ctx.fillStyle = drawing.color || '#ff0000';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 1.0;
      ctx.shadowColor = drawing.color || '#ff0000';
      ctx.shadowBlur = 2;

      if (drawing.tool === 'pen' && drawing.points && drawing.points.length > 0) {
        ctx.beginPath();
        // Convert percentage to pixels
        ctx.moveTo(drawing.points[0].x * w, drawing.points[0].y * h);
        for (let i = 1; i < drawing.points.length; i++) {
          ctx.lineTo(drawing.points[i].x * w, drawing.points[i].y * h);
        }
        ctx.stroke();
      } else if (drawing.tool === 'circle') {
        ctx.beginPath();
        // Convert percentage to pixels
        const cx = drawing.cx * w;
        const cy = drawing.cy * h;
        const r = Math.max(5, drawing.r * Math.min(w, h));
        ctx.arc(cx, cy, r, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (drawing.tool === 'rectangle') {
        // Convert percentage to pixels
        const x = drawing.x * w;
        const y = drawing.y * h;
        const rw = Math.max(10, drawing.width * w);
        const rh = Math.max(10, drawing.height * h);
        ctx.strokeRect(x, y, rw, rh);
      } else if (drawing.tool === 'text' && drawing.text) {
        ctx.font = `bold ${Math.max(14, drawing.fontSize || 16)}px Arial`;
        // Convert percentage to pixels
        ctx.fillText(drawing.text, drawing.x * w, drawing.y * h);
      }

      ctx.restore(); // Restore context state
    });

    // Draw current path if drawing (preview) - also in percentage
    if (isDrawing && currentPath.length > 0 && drawingTool === 'pen') {
      ctx.save();
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 0.8;
      ctx.shadowColor = '#ff0000';
      ctx.shadowBlur = 1;

      if (currentPath.length > 0) {
        ctx.beginPath();
        // Convert percentage to pixels
        ctx.moveTo((currentPath[0]?.x || 0) * w, (currentPath[0]?.y || 0) * h);
        for (let i = 1; i < currentPath.length; i++) {
          const point = currentPath[i];
          if (point) {
            ctx.lineTo(point.x * w, point.y * h);
          }
        }
        ctx.stroke();
      }
      ctx.stroke();
      ctx.restore();
    }
  }, [drawings, isDrawing, currentPath, canvasSize, drawingTool]);

  // Drawing functions - store both viewport coordinates and texture coordinates for stable 360 saves
  const getRelativePosition = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    // Return percentage (0-1) instead of absolute pixels
    // This ensures drawings stay in correct relative position when canvas resizes
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    };
  };

  const worldPointToTexturePoint = useCallback((worldPoint: any) => {
    const panorama = currentPanoramaRef.current;

    if (!panorama || !worldPoint) return null;

    try {
      const localPoint = panorama.worldToLocal(worldPoint.clone()).normalize();
      const horizontalAngle = Math.atan2(localPoint.z, -localPoint.x);
      const latitude = Math.asin(Math.max(-1, Math.min(1, localPoint.y)));
      const normalizedX = horizontalAngle >= 0
        ? horizontalAngle / (2 * Math.PI)
        : (horizontalAngle + 2 * Math.PI) / (2 * Math.PI);

      return {
        x: Math.min(1, Math.max(0, normalizedX)),
        y: Math.min(1, Math.max(0, (Math.PI / 2 - latitude) / Math.PI)),
        localX: localPoint.x,
        localY: localPoint.y,
        localZ: localPoint.z
      };
    } catch (error) {
      // Bilerek toast/banner YOK: bu dönüşüm her fare hareketinde çalışır; hata
      // gösterimi saniyede onlarca bildirim üretirdi. Kayıp graceful: null dönünce
      // kaydetmede saklanan kamera matrisiyle (fallback) doğru koordinat üretilir.
      console.error('[360] Failed to convert world point to texture point:', error);
      return null;
    }
  }, []);

  const viewportToTexturePoint = useCallback((vx: number, vy: number) => {
    const THREE = (window as any).THREE;
    const panorama = currentPanoramaRef.current;
    const camera = viewerRef.current?.camera || viewerRef.current?.getCamera?.();

    if (!THREE || !panorama || !camera) return null;

    try {
      if (typeof camera.updateProjectionMatrix === 'function') {
        camera.updateProjectionMatrix();
      }
      if (typeof camera.updateMatrixWorld === 'function') {
        camera.updateMatrixWorld(true);
      }
      if (typeof panorama.updateMatrixWorld === 'function') {
        panorama.updateMatrixWorld(true);
      }

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(2 * vx - 1, 1 - 2 * vy), camera);

      const intersects = [
        ...raycaster.intersectObject(panorama, true),
        ...raycaster.intersectObjects(panorama.children || [], true)
      ];
      const hit = intersects.find((intersection: any) => intersection.point);

      if (hit?.point) {
        return worldPointToTexturePoint(hit.point);
      }
    } catch (error) {
      // Bilerek toast/banner YOK: aynı gerekçe -- fare hareketi başına çalışır ve
      // başarısızlık kaydetmede fallback kamera yoluyla telafi edilir.
      console.error('[360] Failed to resolve texture coordinate:', error);
    }

    return null;
  }, [worldPointToTexturePoint]);

  const getDrawingPoint = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const viewport = getRelativePosition(e);
    const texture = viewportToTexturePoint(viewport.x, viewport.y);

    return {
      ...viewport,
      textureX: texture?.x,
      textureY: texture?.y,
      localX: (texture as any)?.localX,
      localY: (texture as any)?.localY,
      localZ: (texture as any)?.localZ
    };
  }, [viewportToTexturePoint]);

  const handleDrawingMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode) return;
    e.preventDefault();
    e.stopPropagation();
    
    const pos = getDrawingPoint(e);
    if (drawingTool === 'text') {
      setTextPosition(pos);
      const containerRect = containerRef.current?.getBoundingClientRect();
      setTextModalPosition({
        x: containerRect ? e.clientX - containerRect.left : e.clientX,
        y: containerRect ? e.clientY - containerRect.top : e.clientY
      });
      setShowTextInput(true);
      return;
    }
    
    setIsDrawing(true);
    setStartPoint(pos);
    
    if (drawingTool === 'pen') {
      setCurrentPath([pos]);
    }
  };

  const handleDrawingMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode || !isDrawing) return;
    e.preventDefault();

    const pos = getDrawingPoint(e);

    if (drawingTool === 'pen') {
      setCurrentPath(prev => [...prev, pos]);
    } else if (drawingTool === 'circle' && startPoint) {
      // Draw preview on canvas
      const canvas = drawingCanvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (ctx && canvas) {
        const w = canvas.width;
        const h = canvas.height;

        // Redraw everything
        ctx.clearRect(0, 0, w, h);

        // Redraw existing drawings (convert percentage to pixels)
        drawings.forEach(drawing => {
          ctx.strokeStyle = drawing.color || '#ff0000';
          ctx.lineWidth = drawing.strokeWidth || 3;
          ctx.fillStyle = drawing.color || '#ff0000';

          if (drawing.tool === 'pen' && drawing.points) {
            ctx.beginPath();
            drawing.points.forEach((point: {x: number, y: number}, idx: number) => {
              const px = point.x * w;
              const py = point.y * h;
              if (idx === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.stroke();
          } else if (drawing.tool === 'circle') {
            ctx.beginPath();
            ctx.arc(drawing.cx * w, drawing.cy * h, drawing.r * Math.min(w, h), 0, 2 * Math.PI);
            ctx.stroke();
          } else if (drawing.tool === 'rectangle') {
            ctx.strokeRect(drawing.x * w, drawing.y * h, drawing.width * w, drawing.height * h);
          } else if (drawing.tool === 'text') {
            ctx.font = `bold ${(drawing.fontSize || 16)}px Arial`;
            ctx.fillText(drawing.text, drawing.x * w, drawing.y * h);
          }
        });

        // Draw preview (percentage to pixels)
        const radius = Math.sqrt(Math.pow((pos.x - startPoint.x) * w, 2) + Math.pow((pos.y - startPoint.y) * h, 2));
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(startPoint.x * w, startPoint.y * h, radius, 0, 2 * Math.PI);
        ctx.stroke();
      }
    } else if (drawingTool === 'rectangle' && startPoint) {
      // Draw preview on canvas
      const canvas = drawingCanvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (ctx && canvas) {
        const w = canvas.width;
        const h = canvas.height;

        // Redraw everything
        ctx.clearRect(0, 0, w, h);

        // Redraw existing drawings (convert percentage to pixels)
        drawings.forEach(drawing => {
          ctx.strokeStyle = drawing.color || '#ff0000';
          ctx.lineWidth = drawing.strokeWidth || 3;
          ctx.fillStyle = drawing.color || '#ff0000';

          if (drawing.tool === 'pen' && drawing.points) {
            ctx.beginPath();
            drawing.points.forEach((point: {x: number, y: number}, idx: number) => {
              const px = point.x * w;
              const py = point.y * h;
              if (idx === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.stroke();
          } else if (drawing.tool === 'circle') {
            ctx.beginPath();
            ctx.arc(drawing.cx * w, drawing.cy * h, drawing.r * Math.min(w, h), 0, 2 * Math.PI);
            ctx.stroke();
          } else if (drawing.tool === 'rectangle') {
            ctx.strokeRect(drawing.x * w, drawing.y * h, drawing.width * w, drawing.height * h);
          } else if (drawing.tool === 'text') {
            ctx.font = `bold ${(drawing.fontSize || 16)}px Arial`;
            ctx.fillText(drawing.text, drawing.x * w, drawing.y * h);
          }
        });

        // Draw preview (percentage to pixels)
        const x = Math.min(startPoint.x, pos.x) * w;
        const y = Math.min(startPoint.y, pos.y) * h;
        const width = Math.abs(pos.x - startPoint.x) * w;
        const height = Math.abs(pos.y - startPoint.y) * h;
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, width, height);
      }
    }
  };

  const handleDrawingMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingMode || !isDrawing || !startPoint) return;
    e.preventDefault();
    
    const pos = getDrawingPoint(e);
    setIsDrawing(false);
    
    const newDrawing: any = {
      id: Date.now(),
      tool: drawingTool,
      color: '#ff0000',
      strokeWidth: 5
    };

    if (drawingTool === 'pen') {
      newDrawing.points = currentPath;
    } else if (drawingTool === 'circle') {
      const radius = Math.sqrt(Math.pow(pos.x - startPoint.x, 2) + Math.pow(pos.y - startPoint.y, 2));
      newDrawing.cx = startPoint.x;
      newDrawing.cy = startPoint.y;
      newDrawing.r = radius;
      const numSamples = 64;
      newDrawing.texturePoints = Array.from({ length: numSamples }, (_, i) => {
        const angle = (2 * Math.PI * i) / numSamples;
        const sampleX = startPoint.x + radius * Math.cos(angle);
        const sampleY = startPoint.y + radius * Math.sin(angle);
        const texturePoint = viewportToTexturePoint(sampleX, sampleY);
        return texturePoint ? { x: texturePoint.x, y: texturePoint.y } : null;
      }).filter(Boolean);
    } else if (drawingTool === 'rectangle') {
      newDrawing.x = Math.min(startPoint.x, pos.x);
      newDrawing.y = Math.min(startPoint.y, pos.y);
      newDrawing.width = Math.abs(pos.x - startPoint.x);
      newDrawing.height = Math.abs(pos.y - startPoint.y);
      const samplesPerEdge = 16;
      const texturePoints: Array<{x: number, y: number}> = [];
      const pushTexturePoint = (vx: number, vy: number) => {
        const texturePoint = viewportToTexturePoint(vx, vy);
        if (texturePoint) {
          texturePoints.push({ x: texturePoint.x, y: texturePoint.y });
        }
      };

      for (let i = 0; i <= samplesPerEdge; i++) {
        const t = i / samplesPerEdge;
        pushTexturePoint(newDrawing.x + t * newDrawing.width, newDrawing.y);
      }
      for (let i = 1; i <= samplesPerEdge; i++) {
        const t = i / samplesPerEdge;
        pushTexturePoint(newDrawing.x + newDrawing.width, newDrawing.y + t * newDrawing.height);
      }
      for (let i = 1; i <= samplesPerEdge; i++) {
        const t = i / samplesPerEdge;
        pushTexturePoint(newDrawing.x + newDrawing.width - t * newDrawing.width, newDrawing.y + newDrawing.height);
      }
      for (let i = 1; i < samplesPerEdge; i++) {
        const t = i / samplesPerEdge;
        pushTexturePoint(newDrawing.x, newDrawing.y + newDrawing.height - t * newDrawing.height);
      }
      newDrawing.texturePoints = texturePoints;
    }

    // Cizim anindaki kamera matrislerini kaydet (kaydetme sirasinda dogru koordinat donusumu icin)
    const cam = viewerRef.current?.camera || viewerRef.current?.getCamera?.();
    if (cam) {
      cam.updateMatrixWorld();
      newDrawing.cameraProjMatrixInv = cam.projectionMatrixInverse.clone();
      newDrawing.cameraMatrixWorld = cam.matrixWorld.clone();
      newDrawing.cameraPosition = cam.position.clone();
    }

    setDrawings(prev => [...prev, newDrawing]);
    setCurrentPath([]);
    setStartPoint(null);
  };

  const clearDrawings = () => {
    setDrawings([]);
  };

  const deleteLastDrawing = () => {
    if (drawings.length > 0) {
      setDrawings(prev => prev.slice(0, -1));
    }
  };

  const handleTextSubmit = () => {
    if (textInput.trim() && textPosition) {
      const newDrawing: any = {
        id: Date.now(),
        tool: 'text',
        text: textInput.trim(),
        x: textPosition.x,
        y: textPosition.y,
        textureX: textPosition.textureX,
        textureY: textPosition.textureY,
        color: '#ff0000',
        fontSize: 16
      };

      // Cizim anindaki kamera matrislerini kaydet
      const cam = viewerRef.current?.camera || viewerRef.current?.getCamera?.();
      if (cam) {
        cam.updateMatrixWorld();
        newDrawing.cameraProjMatrixInv = cam.projectionMatrixInverse.clone();
        newDrawing.cameraMatrixWorld = cam.matrixWorld.clone();
        newDrawing.cameraPosition = cam.position.clone();
      }

      setDrawings(prev => [...prev, newDrawing]);
        setTextInput('');
      setShowTextInput(false);
      setTextPosition(null);
      setTextModalPosition(null);
    }
  };

  // Viewport koordinatlarini (0-1) equirectangular goruntu piksellerine donusturur
  const viewportToEquirectangular = (
    vx: number, vy: number,
    cam: any, // THREE.PerspectiveCamera (or stored matrices)
    imgWidth: number, imgHeight: number,
    storedProjMatrixInv?: any, storedMatrixWorld?: any, storedPosition?: any
  ): { x: number; y: number } => {
    const THREE = (window as any).THREE;
    const ndcX = 2 * vx - 1;
    const ndcY = 1 - 2 * vy;
    const vec = new THREE.Vector3(ndcX, ndcY, 0.5);

    if (storedProjMatrixInv && storedMatrixWorld && storedPosition) {
      // Saklanan matrislerle manuel unproject
      vec.applyMatrix4(storedProjMatrixInv);
      vec.applyMatrix4(storedMatrixWorld);
      const dir = vec.sub(storedPosition.clone()).normalize();
      const horizontalAngle = Math.atan2(dir.z, -dir.x);
      const latitude = Math.asin(Math.max(-1, Math.min(1, dir.y)));
      const normalizedX = horizontalAngle >= 0
        ? horizontalAngle / (2 * Math.PI)
        : (horizontalAngle + 2 * Math.PI) / (2 * Math.PI);
      return {
        x: normalizedX * imgWidth,
        y: ((Math.PI / 2 - latitude) / Math.PI) * imgHeight
      };
    } else {
      // Canli kamera ile unproject
      vec.unproject(cam);
      const dir = vec.sub(cam.position).normalize();
      const horizontalAngle = Math.atan2(dir.z, -dir.x);
      const latitude = Math.asin(Math.max(-1, Math.min(1, dir.y)));
      const normalizedX = horizontalAngle >= 0
        ? horizontalAngle / (2 * Math.PI)
        : (horizontalAngle + 2 * Math.PI) / (2 * Math.PI);
      return {
        x: normalizedX * imgWidth,
        y: ((Math.PI / 2 - latitude) / Math.PI) * imgHeight
      };
    }
  };

  // Equirectangular polyline cizer (longitude wrap handling)
  const drawEquirectangularPolyline = (
    ctx: CanvasRenderingContext2D,
    points: Array<{x: number, y: number}>,
    imgWidth: number, close: boolean = false
  ) => {
    if (points.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(points[0]!.x, points[0]!.y);
    for (let i = 1; i < points.length; i++) {
      if (Math.abs(points[i]!.x - points[i - 1]!.x) > imgWidth * 0.4) {
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(points[i]!.x, points[i]!.y);
      } else {
        ctx.lineTo(points[i]!.x, points[i]!.y);
      }
    }
    if (close && points.length > 2 && Math.abs(points[0]!.x - points[points.length - 1]!.x) <= imgWidth * 0.4) {
      ctx.lineTo(points[0]!.x, points[0]!.y);
    }
    ctx.stroke();
  };

  const saveDrawingsToWasabi = async () => {
    // Çift gönderim koruması: istek uçarken buton da disabled.
    if (isSaving) return;

    if (drawings.length === 0) {
      // Doğrulama UYARISI (sarı), hata değil -- eskiden yeşil kutuda çıkıyordu.
      notifyWarning('Kaydedilecek çizim yok', 'Önce fotoğrafın üzerine bir çizim ekleyin.') // TODO(i18n)
      return;
    }

    const currentPhoto = photos[currentPhotoIndex];
    if (!currentPhoto) {
      notifyWarning('Aktif fotoğraf bulunamadı', 'Kaydetmek için soldaki listeden bir fotoğraf seçin.') // TODO(i18n)
      return;
    }

    setIsSaving(true);
    try {

      // Kaydetmede TAM çözünürlüklü equirectangular görüntü gerekir (çizim
      // koordinatları normalize edilmiş; imgW/imgH tam çözünürlükle çarpılır).
      // Same-origin proxy passthrough (pano/w YOK) → resize'sız tam çözünürlük,
      // Wasabi CORS'a takılmadan ve canvas'ı kirletmeden.
      const rawImageUrl = selectedPhoto?.url || currentPhoto.url;
      const imageUrlToLoad = buildImageProxyUrl(rawImageUrl);
      log360('Loading full-res equirectangular image for save via proxy', { url: imageUrlToLoad });

      const img = new Image();
      img.crossOrigin = 'anonymous';

      try {
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          // onerror ham bir DOM Event verir; kullanıcıya dökülmemesi için burada
          // kendi Türkçe mesajımızla ayrıca yakalanıyor.
          img.onerror = () => reject(new Error('panorama image load failed'));
          img.src = imageUrlToLoad;
        });
      } catch (imageError) {
        logError('Turbine360.saveDrawings.loadImage', imageError);
        notifyError(SAVE_ERROR_TITLE, 'Fotoğrafın tam çözünürlüklü hâli yüklenemedi. Bağlantınızı kontrol edip tekrar deneyin.'); // TODO(i18n)
        return;
      }

      // Canvas'i equirectangular goruntu boyutunda olustur
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        // Eskiden sessiz `return`: kullanıcıya hiçbir şey olmamış gibi görünüyordu.
        console.error('❌ 2D canvas context alınamadı');
        notifyError(SAVE_ERROR_TITLE, 'Tarayıcı çizim yüzeyi oluşturamadı. Sayfayı yenileyip tekrar deneyin.'); // TODO(i18n)
        return;
      }

      // Orijinal goruntyu canvas'a ciz
      ctx.drawImage(img, 0, 0);

      const imgW = img.width;
      const imgH = img.height;

      // Fallback kamera (cizimde saklanan matris yoksa)
      const viewer = viewerRef.current;
      const fallbackCamera = viewer?.camera || viewer?.getCamera?.();
      if (fallbackCamera) fallbackCamera.updateMatrixWorld();

      // Cizgi kalinligi olcekleme (equirectangular goruntu viewport'tan buyuk)
      const lineWidthScale = Math.max(1, imgW / canvasSize.width);

      // Her cizimi equirectangular koordinatlara donusturup ciz
      drawings.forEach(drawing => {
        ctx.strokeStyle = drawing.color || '#ff0000';
        ctx.lineWidth = Math.max(2, (drawing.strokeWidth || 3) * lineWidthScale);
        ctx.fillStyle = drawing.color || '#ff0000';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Cizim anindaki kamera matrislerini kullan (veya fallback)
        const hasSavedCamera = drawing.cameraProjMatrixInv && drawing.cameraMatrixWorld && drawing.cameraPosition;
        const convertPoint = (vx: number, vy: number) => {
          if (hasSavedCamera) {
            return viewportToEquirectangular(vx, vy, null, imgW, imgH,
              drawing.cameraProjMatrixInv, drawing.cameraMatrixWorld, drawing.cameraPosition);
          } else if (fallbackCamera) {
            return viewportToEquirectangular(vx, vy, fallbackCamera, imgW, imgH);
          }
          // Son care: viewport yuzdesini direkt uygula (eski davranis)
          return { x: vx * imgW, y: vy * imgH };
        };

        if (drawing.tool === 'pen' && drawing.points) {
          const eqPoints = drawing.points.map((p: {x: number, y: number, textureX?: number, textureY?: number}) => {
            if (typeof p.textureX === 'number' && typeof p.textureY === 'number') {
              return { x: p.textureX * imgW, y: p.textureY * imgH };
            }
            return convertPoint(p.x, p.y);
          });
          drawEquirectangularPolyline(ctx, eqPoints, imgW);

        } else if (drawing.tool === 'circle' && Array.isArray(drawing.texturePoints) && drawing.texturePoints.length > 1) {
          const eqCirclePoints = drawing.texturePoints.map((p: {x: number, y: number}) => ({
            x: p.x * imgW,
            y: p.y * imgH
          }));
          drawEquirectangularPolyline(ctx, eqCirclePoints, imgW, true);

        } else if (drawing.tool === 'circle') {
          // Viewport dairesini 64 sample noktayla equirectangular polygon'a donustur
          const numSamples = 64;
          const rPixels = drawing.r * Math.min(canvasSize.width, canvasSize.height);
          const rxNorm = rPixels / canvasSize.width;
          const ryNorm = rPixels / canvasSize.height;
          const eqCirclePoints: Array<{x: number, y: number}> = [];
          for (let i = 0; i < numSamples; i++) {
            const angle = (2 * Math.PI * i) / numSamples;
            const vx = drawing.cx + rxNorm * Math.cos(angle);
            const vy = drawing.cy + ryNorm * Math.sin(angle);
            eqCirclePoints.push(convertPoint(vx, vy));
          }
          drawEquirectangularPolyline(ctx, eqCirclePoints, imgW, true);

        } else if (drawing.tool === 'rectangle' && Array.isArray(drawing.texturePoints) && drawing.texturePoints.length > 1) {
          const eqRectPoints = drawing.texturePoints.map((p: {x: number, y: number}) => ({
            x: p.x * imgW,
            y: p.y * imgH
          }));
          drawEquirectangularPolyline(ctx, eqRectPoints, imgW, true);

        } else if (drawing.tool === 'rectangle') {
          // Dikdortgenin her kenarini sample noktalarla ciz
          const samplesPerEdge = 16;
          const eqRectPoints: Array<{x: number, y: number}> = [];
          // Ust kenar
          for (let i = 0; i <= samplesPerEdge; i++) {
            const t = i / samplesPerEdge;
            eqRectPoints.push(convertPoint(drawing.x + t * drawing.width, drawing.y));
          }
          // Sag kenar
          for (let i = 1; i <= samplesPerEdge; i++) {
            const t = i / samplesPerEdge;
            eqRectPoints.push(convertPoint(drawing.x + drawing.width, drawing.y + t * drawing.height));
          }
          // Alt kenar (ters)
          for (let i = 1; i <= samplesPerEdge; i++) {
            const t = i / samplesPerEdge;
            eqRectPoints.push(convertPoint(drawing.x + drawing.width - t * drawing.width, drawing.y + drawing.height));
          }
          // Sol kenar (ters)
          for (let i = 1; i < samplesPerEdge; i++) {
            const t = i / samplesPerEdge;
            eqRectPoints.push(convertPoint(drawing.x, drawing.y + drawing.height - t * drawing.height));
          }
          drawEquirectangularPolyline(ctx, eqRectPoints, imgW, true);

        } else if (drawing.tool === 'text') {
          const eqPos =
            typeof drawing.textureX === 'number' && typeof drawing.textureY === 'number'
              ? { x: drawing.textureX * imgW, y: drawing.textureY * imgH }
              : convertPoint(drawing.x, drawing.y);
          const fontSize = Math.max(12, (drawing.fontSize || 16) * lineWidthScale);
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.fillText(drawing.text, eqPos.x, eqPos.y);
        }
      });

      // Canvas'ı blob'a çevir. toBlob null dönebilir; eskiden `blob!` ile
      // görmezden geliniyor ve hata ileride anlamsız bir yerde patlıyordu.
      const finalBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.95);
      });

      if (!finalBlob) {
        console.error('❌ Düzenlenmiş görüntü blob\'a çevrilemedi');
        notifyError(SAVE_ERROR_TITLE, 'Düzenlenmiş görüntü oluşturulamadı. Lütfen tekrar deneyin.'); // TODO(i18n)
        return;
      }

      // Versiyonlama için fotoğraf ID'si zorunlu (DB kaydı olmayan eski Wasabi
      // fotoğrafları desteklenmiyor — eski endpoint de photoId'siz isteği reddediyordu)
      if (!currentPhoto.id) {
        // Doğrulama uyarısı (SARI) -- eskiden yeşil "başarılı" kutusunda çıkıyordu.
        notifyWarning('Bu fotoğraf düzenlemeyi desteklemiyor', 'Fotoğrafın kayıt ID\'si olmadığı için yeni versiyon oluşturulamıyor.') // TODO(i18n)
        return;
      }

      try {
        log360('[360 Save] Submitting edited photo via direct upload', {
          turbineId,
          photoId: currentPhoto.id,
          blobSize: finalBlob.size
        })

        // Doğrudan Wasabi'ye presigned PUT ile yükle (Vercel'in 4.5MB istek
        // gövdesi limitine takılmaz; büyük 360 görüntüleri sorunsuz kaydedilir)
        const result = await turbineService.saveEditedPhotoVersion(
          turbineId,
          currentPhoto.id,
          finalBlob,
          'image/jpeg'
        );
        log360('[360 Save] Response', result)
        if (result.success) {
          // Çizimleri temizle
          setDrawings([])
          setIsDrawingMode(false)

          // Cache temizle ve yeni veriyi çek
          turbineService.clearPhotoCaches(turbineId)

          // Show notification for immediate feedback (like operator screen)
          log360('Save successful, showing success notification')
          notifySuccess('Fotoğraf kaydedildi', 'Çizimli fotoğraf başarıyla düzenlendi ve yeni versiyon olarak kaydedildi.') // TODO(i18n)

          // Dropdown'u hemen güncellemek için versions'ı yeniden yükle
          // Bu callback parent component'taki version effect'ini tetikler
          if (onVersionsRefresh) {
            log360('Triggering versions refresh after save')
            onVersionsRefresh()
          }

          // Refresh callback'i tetikle - notification görüntülendikten sonra geciktir
          if (onRefresh) {
            setTimeout(() => {
              onRefresh()
            }, 500) // Notification'ın görünmesi için 500ms bekle
          }

          return;
        }
        // success:false BİR HATADIR: backend mesajı varsa aynen gösterilir.
        notifyError(SAVE_ERROR_TITLE, responseErrorMessage(result, t, SAVE_ERROR_BODY));
      } catch (e) {
        logError('Turbine360.saveDrawings.upload', e);
        notifyError(SAVE_ERROR_TITLE, toUserMessage(e, t, SAVE_ERROR_BODY));
      }
    } catch (error) {
      logError('Turbine360.saveDrawings', error);
      notifyError(SAVE_ERROR_TITLE, toUserMessage(error, t, SAVE_ERROR_BODY));
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation functions
  const goToPrevious = () => {
    if (photos.length === 0) return;
    const newIndex = currentPhotoIndex === 0 ? photos.length - 1 : currentPhotoIndex - 1;
    setCurrentPhotoIndex(newIndex);
    
    // Update selected photo in sidebar
    if (photos[newIndex] && onPhotoChange) {
      onPhotoChange(photos[newIndex]);
    }
  };

  const goToNext = () => {
    if (photos.length === 0) return;
    const newIndex = currentPhotoIndex === photos.length - 1 ? 0 : currentPhotoIndex + 1;
    setCurrentPhotoIndex(newIndex);
    
    // Update selected photo in sidebar
    if (photos[newIndex] && onPhotoChange) {
      onPhotoChange(photos[newIndex]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'F11' || (e.key === 'f' && !e.metaKey && !e.ctrlKey)) {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'Escape' && isFullscreen) {
        // The 'fullscreenchange' event will handle the state update
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (isDrawingMode) {
          deleteLastDrawing();
        }
      } else if (e.key === 'Enter' && showTextInput) {
        handleTextSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPhotoIndex, photos.length, isFullscreen, isDrawingMode, showTextInput]);

  // NOT: isLoading ve photos.length===0 durumları artık erken return ile
  // container'ı unmount ETMİYOR (viewer kalıcı olmalı; unmount edilirse yeniden
  // yaratılır ve async doku yüklemesiyle yarışıp çökerdi). Bu durumlar aşağıda
  // overlay olarak gösteriliyor.
  return (
    <div className="absolute inset-0">
      <div className="w-full h-full bg-gray-900 overflow-hidden relative">
        {/* Panolens.js 360° Container */}
        <div
          ref={containerRef}
          className="absolute inset-0 w-full h-full"
        />

        {/* Yükleme göstergesi: ilk foto çekimi (isLoading) VEYA panorama-geçiş
            doku yüklemesi (isPanoramaLoading). Blade/oda değişiminde eski görsel
            altta durur (viewer kalıcı) ve bu overlay dimleyerek "yükleniyor"
            gösterir; yeni görsel gelince cross-fade ile açılır. z-index toolbar
            (z-30) altında. */}
        {(isLoading || isPanoramaLoading) && (
          <div className="absolute inset-0 z-[15] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm pointer-events-none">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <div className="text-sm">
                {isLoading ? 'Panoramik fotoğraflar yükleniyor...' : 'Panorama yükleniyor...'}
              </div>
            </div>
          </div>
        )}

        {/* Foto yoksa (yükleme bittiğinde) boş durum overlay'i.
            Hata varken GÖSTERİLMEZ: "hata != boş veri" — yüklenemeyen liste
            "bu odada fotoğraf yok" gibi görünmemeli, aşağıdaki banner çıkar. */}
        {!isLoading && photos.length === 0 && !loadError && (
          <div className="absolute inset-0 z-[16] flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <div className="text-gray-600">Bu odada fotoğraf bulunamadı</div>
            </div>
          </div>
        )}

        {/* Hata katmanı: liste / görüntüleyici / panorama hataları kullanıcının
            baktığı yüzeyde gösterilir. Eskiden hepsi yalnızca console.error'a
            gidiyordu ve kullanıcı boş ya da siyah ekrana bakıyordu. */}
        {(loadError || viewerError || panoramaError) && (
          <div className="absolute top-4 left-4 right-4 sm:right-auto sm:max-w-md z-40 space-y-2">
            {loadError && (
              <ErrorBanner
                message={loadError}
                className="shadow-lg"
                onRetry={() => setPhotosReloadKey(prev => prev + 1)}
                onDismiss={() => setLoadError(null)}
              />
            )}
            {viewerError && (
              <ErrorBanner
                message={viewerError}
                className="shadow-lg"
                onDismiss={() => setViewerError(null)}
              />
            )}
            {panoramaError && !viewerError && (
              <ErrorBanner
                message={panoramaError}
                className="shadow-lg"
                onRetry={retryPanorama}
                onDismiss={() => setPanoramaError(null)}
              />
            )}
          </div>
        )}

        {/* Drawing Canvas Overlay - Perfectly aligned with panorama */}
        <canvas
          ref={drawingCanvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          className={`absolute ${isDrawingMode ? 'cursor-crosshair' : 'pointer-events-none'}`}
          style={{
            zIndex: isDrawingMode ? 20 : 5,
            touchAction: 'none',
            width: `${canvasSize.width}px`,
            height: `${canvasSize.height}px`,
            imageRendering: 'crisp-edges',
            pointerEvents: isDrawingMode ? 'auto' : 'none',
            position: 'absolute',
            top: 0,
            left: 0
          }}
          onMouseDown={handleDrawingMouseDown}
          onMouseMove={handleDrawingMouseMove}
          onMouseUp={handleDrawingMouseUp}
        />

        {/* Drawing Toolbar */}
        <div className="absolute top-4 right-4 z-30 space-y-3">
          <div className="bg-black/70 backdrop-blur-sm rounded-xl p-2 flex items-center space-x-2 shadow-lg border border-white/10">
            <button
              onClick={() => setIsDrawingMode(prev => !prev)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isDrawingMode ? 'bg-orange-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
              title={isDrawingMode ? 'Çizimi kapat' : 'Çizimi aç'}
            >
              {isDrawingMode ? 'Çizim Açık' : 'Çizim Kapalı'}
            </button>
            <div className={`flex items-center space-x-1 ${isDrawingMode ? '' : 'pointer-events-none opacity-50'}`}>
              <button onClick={() => setDrawingTool('pen')} className={`px-2 py-1 rounded-md text-white ${drawingTool === 'pen' ? 'bg-orange-500' : 'bg-white/10 hover:bg-white/20'}`} title="Kalem">Kalem</button>
              <button onClick={() => setDrawingTool('circle')} className={`px-2 py-1 rounded-md text-white ${drawingTool === 'circle' ? 'bg-orange-500' : 'bg-white/10 hover:bg-white/20'}`} title="Daire">Daire</button>
              <button onClick={() => setDrawingTool('rectangle')} className={`px-2 py-1 rounded-md text-white ${drawingTool === 'rectangle' ? 'bg-orange-500' : 'bg-white/10 hover:bg-white/20'}`} title="Dikdörtgen">Dikdörtgen</button>
              <button onClick={() => setDrawingTool('text')} className={`px-2 py-1 rounded-md text-white ${drawingTool === 'text' ? 'bg-orange-500' : 'bg-white/10 hover:bg-white/20'}`} title="Yazı">Yazı</button>
            </div>
            <button
              onClick={clearDrawings}
              disabled={!isDrawingMode}
              className={`px-2 py-1 rounded-md text-white ${isDrawingMode ? 'bg-white/10 hover:bg-white/20' : 'bg-white/5 opacity-50 cursor-not-allowed'}`}
              title="Tüm çizimleri temizle"
            >
              Temizle
            </button>
            <button
              onClick={saveDrawingsToWasabi}
              disabled={isSaving}
              className={`px-3 py-1.5 rounded-lg text-white font-medium ${isSaving ? 'bg-green-600/60 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              title="Çizimleri fotoğrafın üzerine kaydet"
            >
              {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>

        {/* Text Input Modal */}
        {showTextInput && textPosition && textModalPosition && (
          <div
            className="absolute bg-white p-3 rounded-lg shadow-lg border z-30"
            style={{
              left: textModalPosition.x + 10,
              top: textModalPosition.y + 10
            }}
          >
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
              placeholder="Metin yazın..."
              className="border rounded px-2 py-1 text-sm w-32"
                autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTextSubmit();
                } else if (e.key === 'Escape') {
                  setShowTextInput(false);
                    setTextInput('');
                    setTextPosition(null);
                    setTextModalPosition(null);
                }
              }}
            />
            <div className="flex gap-1 mt-2">
              <button
                onClick={handleTextSubmit}
                className="bg-blue-500 text-white px-2 py-1 text-xs rounded"
                >
                  Ekle
                </button>
                <button
                  onClick={() => {
                  setShowTextInput(false);
                    setTextInput('');
                    setTextPosition(null);
                    setTextModalPosition(null);
                  }}
                className="bg-gray-500 text-white px-2 py-1 text-xs rounded"
                >
                  İptal
                </button>
            </div>
          </div>
        )}

                        {/* Edit modal removed as requested */}

                        {/* Bildirimler artık global NotificationProvider (toast) katmanından
                            geliyor. Lokal ToastNotification kaldırıldı: `type` prop'u olmadığı
                            için HATALARI yeşil "başarılı" kutusunda gösteriyordu. */}

                        {/* Enhanced Navigation Controls */}
        <div className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-6 bg-black/90 rounded-full px-8 py-4 z-10`}>
          <button
            onClick={goToPrevious}
            disabled={photos.length <= 1}
            className="p-3 text-white hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white/10 rounded-full hover:bg-white/20"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="text-center">
            <div className="text-white text-lg font-bold">
              {photos.length === 0 
                ? "Fotoğraf Bulunamadı" 
                : `${currentPhotoIndex + 1} / ${photos.length}`
              }
            </div>
            <div className="text-gray-300 text-xs">
              {photos.length === 0 
                ? "Seçili blade ve oda için panoramik fotoğraf bulunmuyor"
                : "Sıralı Gezinme • ← → • F11 Tam Ekran"
              }
            </div>
          </div>
          
          <button
            onClick={goToNext}
            disabled={photos.length <= 1}
            className="p-3 text-white hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white/10 rounded-full hover:bg-white/20"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Edit buttons and fullscreen removed */}
      </div>
    </div>
  );
};

export default Enhanced360Viewer
